import CloseIcon from '@mui/icons-material/Close';
import { Box, DialogTitle, IconButton, SxProps, Theme, Typography } from '@mui/material';
import React, { FunctionComponent, ReactNode } from 'react';

interface DialogChromeHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  onClose: (event?: any) => void;
  sticky?: boolean;
  sx?: SxProps<Theme>;
  titleId?: string;
  closeButtonAriaLabel?: string;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement> & { ref?: React.Ref<HTMLDivElement> };
}

interface DialogChromeFooterProps {
  children: ReactNode;
  sticky?: boolean;
  sx?: SxProps<Theme>;
}

export const dialogActionButtonSx = {
  minWidth: 112,
};

export const dialogPrimaryActionSx = {
  minWidth: 148,
};

export const DialogChromeHeader: FunctionComponent<React.PropsWithChildren<DialogChromeHeaderProps>> = ({
  title,
  subtitle,
  onClose,
  sticky = false,
  sx,
  titleId,
  closeButtonAriaLabel = 'Close dialog',
  dragHandleProps,
}) => {
  return (
    <DialogTitle
      component="div"
      id={titleId ? `${titleId}-header` : undefined}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 2,
        pr: 1.5,
        py: 2,
        pl: 3,
        ...(sticky
          ? {
              position: 'sticky',
              top: 0,
              zIndex: 2,
              backgroundColor: 'background.paper',
              borderBottom: 1,
              borderColor: 'divider',
            }
          : {}),
        ...sx,
      }}
    >
      <Box {...dragHandleProps} sx={{ minWidth: 0, flex: 1 }}>
        <Typography id={titleId} variant="h1">
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 720 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      <IconButton aria-label={closeButtonAriaLabel} onClick={onClose} size="medium" sx={{ flexShrink: 0 }}>
        <CloseIcon />
      </IconButton>
    </DialogTitle>
  );
};

export const DialogChromeFooter: FunctionComponent<React.PropsWithChildren<DialogChromeFooterProps>> = ({
  children,
  sticky = false,
  sx,
}) => {
  return (
    <Box
      sx={{
        px: 3,
        py: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 1,
        borderTop: 1,
        borderColor: 'divider',
        ...(sticky
          ? {
              position: 'sticky',
              bottom: 0,
              zIndex: 2,
              backgroundColor: 'background.paper',
            }
          : {}),
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};
